import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import ApiService from '../../services/api';

const About = () => {
  const [teamMembers, setTeamMembers] = useState([]);

  const [aboutContent, setAboutContent] = useState({
    title: 'About Us',
    subtitle: 'Connecting Talent with Opportunity',
    missionTitle: 'Our Mission',
    missionBody:
      'To revolutionize the way people connect with career opportunities by providing an innovative, user-friendly platform that empowers both job seekers and employers to achieve their goals.',
    body:
      "Welcome to JobIT, your premier destination for career growth and professional opportunities. We're dedicated to bridging the gap between talented individuals and leading employers, creating meaningful connections that drive success.\n\nOur platform serves as a comprehensive ecosystem where job seekers can discover opportunities, employers can find exceptional talent, and service providers can showcase their expertise. We're committed to fostering a community that values innovation, growth, and mutual success.",
  });

  useEffect(() => {
    let mounted = true;
    const overlay = (next) => {
      const about = next?.about || {};
      setAboutContent((prev) => {
        const pick = (v, prevV) => (typeof v === 'string' && v.trim().length > 0 ? v : prevV);
        return {
          ...prev,
          title: pick(about.title, prev.title),
          subtitle: pick(about.subtitle, prev.subtitle),
          missionTitle: pick(about.missionTitle, prev.missionTitle),
          missionBody: pick(about.missionBody, prev.missionBody),
          body: pick(about.body, prev.body),
        };
      });
      const nextTeam = Array.isArray(about.teamMembers) ? about.teamMembers : [];
      setTeamMembers(nextTeam);
    };
    const load = async () => {
      try {
        const resp = await ApiService.getSystemSettings();
        if (!mounted) return;
        overlay(resp || {});
        try { localStorage.setItem('systemSettings', JSON.stringify(resp)); } catch (_) {}
        try { localStorage.setItem('adminSettings', JSON.stringify(resp)); } catch (_) {}
      } catch (_) {
        try {
          const raw = localStorage.getItem('systemSettings') || localStorage.getItem('adminSettings');
          const parsed = raw ? JSON.parse(raw) : null;
          if (mounted && parsed) overlay(parsed);
        } catch (_) {}
      }
    };
    load();
    // Listen for admin settings updates
    const onSettingsUpdated = (e) => {
      const detail = e?.detail || null;
      if (!detail || !mounted || !detail.about) return;
      overlay(detail);
    };
    try { window.addEventListener('systemSettingsUpdated', onSettingsUpdated); } catch (_) {}
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" component="h1" gutterBottom>
          {aboutContent.title || 'About Us'}
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph>
          {aboutContent.subtitle || 'Connecting Talent with Opportunity'}
        </Typography>
      </Box>

      <Box mb={8}>
        <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
          {aboutContent.body}
        </Typography>
      </Box>

      <Box mb={8}>
        <Typography variant="h4" component="h2" gutterBottom textAlign="center">
          {aboutContent.missionTitle || 'Our Mission'}
        </Typography>
        <Typography variant="body1" paragraph textAlign="center">
          {aboutContent.missionBody}
        </Typography>
      </Box>

      {Array.isArray(teamMembers) && teamMembers.length > 0 && (
        <Box>
          <Typography variant="h4" component="h2" gutterBottom textAlign="center" mb={4}>
            Meet Our Team
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {teamMembers.map((member, index) => (
              <Grid item xs={12} sm={6} md={4} key={`team-member-${index}`}>
                <Card sx={{ height: '100%' }}>
                  <CardMedia
                    component="img"
                    height="300"
                    image={member.image || member.photoUrl || '/logo192.png'}
                    alt={member.name}
                  />
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="h3">
                      {member.name || member.fullName || 'Team Member'}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      {member.role || member.position || ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.description || member.bio || ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default About;